/**
 * Minimal LaTeX validation that preserves complex expressions
 */
export const validateLaTeX = (content: string): string => {
  if (!content) return content;

  return content
    // Fix double-escaped backslashes (common from AI responses)
    .replace(/\\\\\\\\/g, '\\\\')
    // Remove zero-width spaces
    .replace(/\u200B/g, '')
    // Ensure proper spacing around operators
    .replace(/([+\-*/=])([a-zA-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])([+\-*/=])/g, '$1 $2')
    .trim();
};

/**
 * Safe LaTeX cleaning that preserves complex structures and prepares for KaTeX rendering
 */
export const cleanLaTeX = (expression: string): string => {
  if (!expression) return expression;

  // Preserve matrix environments and other complex structures
  const preservedEnvironments = [
    'bmatrix', 'pmatrix', 'vmatrix', 'Bmatrix', 'Vmatrix',
    'matrix', 'array', 'cases', 'aligned', 'gathered', 'split'
  ];

  let cleaned = expression;
  
  // Temporarily replace complex environments to protect them
  const environmentPlaceholders: string[] = [];
  preservedEnvironments.forEach(env => {
    const regex = new RegExp(`\\\\begin\\{${env}\\}[\\s\\S]*?\\\\end\\{${env}\\}`, 'g');
    cleaned = cleaned.replace(regex, match => {
      environmentPlaceholders.push(match);
      return `@@ENV_${environmentPlaceholders.length - 1}@@`;
    });
  });

  // Clean KaTeX-specific delimiters and prepare for rendering
  cleaned = cleaned
    // Remove \(...\) and \[...\] delimiters
    .replace(/\\\(([^\\)]+)\\\)/g, '$1')
    .replace(/\\\[[^\\]+\]/g, (match) => match.replace(/\\\[|\\\]/g, ''))
    // Fix common spacing issues without breaking commands
    .replace(/\\ /g, ' ')
    .replace(/([^\\])([,;:!?])/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    // Handle text and operator commands
    .replace(/\\text\{([^}]+)\}/g, '$1')
    .replace(/\\operatorname\{([^}]+)\}/g, '$1')
    // Fix fractions without braces
    .replace(/\\frac\s+([^{][^\s}]*)\s+([^{][^\s}]*)/g, '\\frac{$1}{$2}')
    // Remove remaining backslashes for non-environment content
    .replace(/\\[a-zA-Z]+\*?(?![{])/g, '')
    .replace(/\\+/g, '')
    .replace(/\{([^{}]*)\}/g, '$1')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();

  // Restore preserved environments
  environmentPlaceholders.forEach((env, index) => {
    cleaned = cleaned.replace(`@@ENV_${index}@@`, env);
  });

  return cleaned;
};

/**
 * Check if content contains LaTeX expressions
 */
export const containsLaTeX = (content: string): boolean => {
  return /\$[^$]+\$|\$\$[^$]+\$\$|\\\(|\\\)|\\\[|\\\]|\\begin\{[^}]+\}/.test(content);
};

/**
 * Extract LaTeX expressions from text including environments
 */
export const extractLaTeXFromText = (text: string): string[] => {
  const inlineMatches = text.match(/\$[^$]+\$/g) || [];
  const displayMatches = text.match(/\$\$[^$]+\$\$/g) || [];
  const blockMatches = text.match(/\\\([^]*?\\\)|\\\[[^]*?\\\]/g) || [];
  const envMatches = text.match(/\\begin\{[^}]+\}[^]*?\\end\{[^}]+\}/g) || [];
  
  return [...inlineMatches, ...displayMatches, ...blockMatches, ...envMatches];
};

/**
 * Check if LaTeX expression is valid (preserves complex structures)
 */
export const isLaTeXValid = (expression: string): boolean => {
  try {
    // Check for proper wrapping or environment structure
    const isInline = expression.startsWith('$') && expression.endsWith('$');
    const isDisplay = expression.startsWith('$$') && expression.endsWith('$$');
    const isBlock = (expression.startsWith('\\(') && expression.endsWith('\\)')) ||
                    (expression.startsWith('\\[') && expression.endsWith('\\]'));
    const isEnv = expression.startsWith('\\begin{') && /\\end\{[^}]+\}$/.test(expression);

    if (!isInline && !isDisplay && !isBlock && !isEnv) return false;

    return true;
  } catch {
    return false;
  }
};

/**
 * Fix common LaTeX errors while preserving complex structures
 */
export const fixCommonLaTeXErrors = (content: string): string => {
  if (!content) return content;

  // Preserve environments first
  const preservedEnvironments: string[] = [];
  const envRegex = /\\begin\{([^}]+)\}[^]*?\\end\{\1\}/g;
  let processed = content;
  
  processed = processed.replace(envRegex, match => {
    preservedEnvironments.push(match);
    return `@@ENV_${preservedEnvironments.length - 1}@@`;
  });

  // Fix issues in non-environment parts
  processed = processed
    // Fix unescaped special characters outside LaTeX
    .replace(/([^\\])([&%$#_{}])/g, (match, p1, p2) => {
      const before = match.substring(0, match.indexOf(p1 + p2));
      if (before.includes('$') || before.includes('\\(') || before.includes('\\[')) {
        return match;
      }
      return p1 + '\\' + p2;
    })
    // Fix fractions without braces
    .replace(/\\frac\s+([^{][^\s}]*)\s+([^{][^\s}]*)/g, '\\frac{$1}{$2}')
    // Ensure proper spacing around operators
    .replace(/([+\-*/=])([a-zA-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])([+\-*/=])/g, '$1 $2');

  // Restore environments
  preservedEnvironments.forEach((env, index) => {
    processed = processed.replace(`@@ENV_${index}@@`, env);
  });

  return processed;
};

/**
 * Safely render LaTeX content with environment preservation
 */
export const safeLaTeXRender = (content: string): string => {
  try {
    const cleaned = validateLaTeX(content);
    const fixed = fixCommonLaTeXErrors(cleaned);
    return fixed;
  } catch (error) {
    console.warn('LaTeX rendering failed, returning original content:', error);
    return content;
  }
};

/**
 * Extract plain text from LaTeX for speech (converts mathematical expressions)
 */
export const latexToSpeechText = (latexContent: string): string => {
  if (!latexContent) return '';

  // Preserve environments to process their content minimally
  const preservedEnvironments: string[] = [];
  const envRegex = /\\begin\{([^}]+)\}[^]*?\\end\{\1\}/g;
  let processed = latexContent;
  
  processed = processed.replace(envRegex, match => {
    // Simplify environment content for speech (e.g., matrices to numbers)
    const simplifiedEnv = match
      .replace(/\\begin\{[^}]*\}/g, '')
      .replace(/\\end\{[^}]*\}/g, '')
      .replace(/\\\\/g, ' ')
      .replace(/\{([^{}]*)\}/g, '$1')
      .replace(/\[([^\]]*)\]/g, '$1')
      .replace(/\\([a-zA-Z]+|\W)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    preservedEnvironments.push(simplifiedEnv);
    return `@@ENV_${preservedEnvironments.length - 1}@@`;
  });

  // Process non-environment parts for natural speech
  processed = processed
    // Remove LaTeX delimiters
    .replace(/\\\(([^\\)]+)\\\)/g, '$1')
    .replace(/\\\[[^\\]+\]/g, (match) => match.replace(/\\\[|\\\]/g, ''))
    .replace(/\$([^$]+)\$/g, '$1')
    .replace(/\$\$([^$]+)\$\$/g, '$1')
    // Convert mathematical expressions to natural language
    .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '$1 divided by $2')
    .replace(/\\dfrac\{([^}]*)\}\{([^}]*)\}/g, '$1 divided by $2')
    .replace(/\\frac\s+([^{][^\s}]*)\s+([^{][^\s}]*)/g, '$1 divided by $2')
    .replace(/\\sqrt\{([^}]*)\}/g, 'square root of $1')
    .replace(/\\cbrt\{([^}]*)\}/g, 'cube root of $1')
    .replace(/\\sqrt\[(\d+)\]\{([^}]+)\}/g, '$1th root of $2')
    .replace(/\^\{([^}]*)\}/g, 'to the power $1')
    .replace(/_\{([^}]*)\}/g, 'subscript $1')
    .replace(/\\text\{([^}]+)\}/g, '$1')
    .replace(/\\operatorname\{([^}]+)\}/g, '$1')
    .replace(/\\int(_?\w*)\{([^{}]+)\}\^\{([^{}]+)\}/g, 'integral from $2 to $3 of')
    .replace(/\\int\b/g, 'integral of')
    .replace(/\\sum(_?\w*)\{([^{}]+)\}\^\{([^{}]+)\}/g, 'sum from $2 to $3 of')
    .replace(/\\sum\b/g, 'sum of')
    .replace(/\\prod(_?\w*)\{([^{}]+)\}\^\{([^{}]+)\}/g, 'product from $2 to $3 of')
    .replace(/\\prod\b/g, 'product of')
    .replace(/\\lim(_?\w*)\{([^{}]+)\}/g, 'limit as $2 of')
    .replace(/\\lim\b/g, 'limit of')
    .replace(/\\partial/g, 'partial derivative of')
    .replace(/\\sin\b/g, 'sine of')
    .replace(/\\cos\b/g, 'cosine of')
    .replace(/\\tan\b/g, 'tangent of')
    .replace(/\\sec\b/g, 'secant of')
    .replace(/\\csc\b/g, 'cosecant of')
    .replace(/\\cot\b/g, 'cotangent of')
    .replace(/\\arcsin\b/g, 'arcsine of')
    .replace(/\\arccos\b/g, 'arccosine of')
    .replace(/\\arctan\b/g, 'arctangent of')
    .replace(/\\sinh\b/g, 'hyperbolic sine of')
    .replace(/\\cosh\b/g, 'hyperbolic cosine of')
    .replace(/\\tanh\b/g, 'hyperbolic tangent of')
    .replace(/\\log\b/g, 'logarithm of')
    .replace(/\\ln\b/g, 'natural log of')
    .replace(/\\lg\b/g, 'log base 10 of')
    .replace(/\\times/g, 'times')
    .replace(/\\div/g, 'divided by')
    .replace(/\\pm/g, 'plus or minus')
    .replace(/\\mp/g, 'minus or plus')
    .replace(/\\approx/g, 'approximately equals')
    .replace(/\\neq/g, 'not equal to')
    .replace(/\\leq/g, 'less than or equal to')
    .replace(/\\geq/g, 'greater than or equal to')
    .replace(/\\lt/g, 'less than')
    .replace(/\\gt/g, 'greater than')
    .replace(/\\infty/g, 'infinity')
    .replace(/\\rightarrow/g, 'approaches')
    .replace(/\\to/g, 'to')
    .replace(/\\in\b/g, 'is in')
    .replace(/\\notin\b/g, 'is not in')
    .replace(/\\subset/g, 'is a subset of')
    .replace(/\\supset/g, 'is a superset of')
    .replace(/\\cup/g, 'union')
    .replace(/\\cap/g, 'intersection')
    .replace(/\\emptyset/g, 'empty set')
    .replace(/\\pi/g, 'pi')
    .replace(/\\alpha/g, 'alpha')
    .replace(/\\beta/g, 'beta')
    .replace(/\\gamma/g, 'gamma')
    .replace(/\\delta/g, 'delta')
    .replace(/\\epsilon/g, 'epsilon')
    .replace(/\\zeta/g, 'zeta')
    .replace(/\\eta/g, 'eta')
    .replace(/\\theta/g, 'theta')
    .replace(/\\iota/g, 'iota')
    .replace(/\\kappa/g, 'kappa')
    .replace(/\\lambda/g, 'lambda')
    .replace(/\\mu/g, 'mu')
    .replace(/\\nu/g, 'nu')
    .replace(/\\xi/g, 'xi')
    .replace(/\\rho/g, 'rho')
    .replace(/\\sigma/g, 'sigma')
    .replace(/\\tau/g, 'tau')
    .replace(/\\upsilon/g, 'upsilon')
    .replace(/\\phi/g, 'phi')
    .replace(/\\chi/g, 'chi')
    .replace(/\\psi/g, 'psi')
    .replace(/\\omega/g, 'omega')
    // Handle remaining braces and brackets
    .replace(/\{([^{}]*)\}/g, '$1')
    .replace(/\[([^\]]*)\]/g, '$1')
    // Clean remaining LaTeX commands and backslashes
    .replace(/\\[a-zA-Z]+\*?(?![{])/g, '')
    .replace(/\\+/g, '')
    // Clean spacing and formatting
    .replace(/\\(?:quad|qquad|,|;|:)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Restore environments (simplified for speech)
  preservedEnvironments.forEach((env, index) => {
    processed = processed.replace(`@@ENV_${index}@@`, env);
  });

  return processed || 'No readable content';
};

/**
 * Validate if content is primarily LaTeX
 */
export const isPrimarilyLaTeX = (content: string): boolean => {
  const latexMatches = extractLaTeXFromText(content);
  const latexLength = latexMatches.join('').length;
  const totalLength = content.length;
  
  return totalLength > 0 && latexLength / totalLength > 0.3;
};

/**
 * Minimal validation that preserves LaTeX structure (especially environments)
 */
export const minimalLaTeXValidation = (content: string): string => {
  if (!content) return content;
  
  return content
    .replace(/\\\\\\\\/g, '\\\\')
    .replace(/\u200B/g, '')
    .trim();
};

/**
 * Check if LaTeX content contains complex environments
 */
export const hasComplexEnvironment = (content: string): boolean => {
  return /\\begin\{(bmatrix|pmatrix|vmatrix|Bmatrix|Vmatrix|matrix|array|cases|aligned|gathered|split)\}/.test(content);
};

/**
 * Get the type of LaTeX environment if present
 */
export const getLaTeXEnvironmentType = (content: string): string | null => {
  const match = content.match(/\\begin\{([^}]+)\}/);
  return match ? match[1] : null;
};

const mathUtils = {
  validateLaTeX,
  cleanLaTeX,
  containsLaTeX,
  extractLaTeXFromText,
  isLaTeXValid,
  fixCommonLaTeXErrors,
  safeLaTeXRender,
  latexToSpeechText,
  isPrimarilyLaTeX,
  minimalLaTeXValidation,
  hasComplexEnvironment,
  getLaTeXEnvironmentType
};

export default mathUtils;